import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * Reusable loading/error overlay for all practice types.
 *
 * @param {boolean}  visible    - show/hide the overlay
 * @param {string}   text       - primary message
 * @param {string}   [subtext]  - optional secondary message
 * @param {'loading'|'error'} [variant='loading'] - visual variant
 * @param {Function} [onRetry]  - when provided (error variant), show "Try Again" button
 * @param {Function} [onClose]  - when provided (error variant), show "Go Back" / secondary button
 * @param {boolean}  [showCloseButton=true] - hide secondary when false (e.g. one-shot retry)
 * @param {string}   [retryLabel] - override "Try Again"
 * @param {string}   [closeLabel] - override "Go Back"
 * @param {string}   [bottomNote] - optional note below the buttons (error variant)
 */

const spin = {
  animate: { rotate: 360 },
  transition: { repeat: Infinity, ease: 'linear' },
};

const WarningIcon = () => (
  <svg
    className="w-10 h-10 max-md:w-8 max-md:h-8 text-[#ff6b6b]"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const LoadingSpinner = () => (
  <div className="relative w-20 h-20 max-md:w-16 max-md:h-16">
    <motion.div
      className="absolute rounded-full border-[3px] border-transparent border-t-[#feca57] w-20 h-20 max-md:w-16 max-md:h-16"
      animate={spin.animate}
      transition={{ ...spin.transition, duration: 1.5 }}
      aria-hidden
    />
    <motion.div
      className="absolute rounded-full border-[3px] border-transparent border-r-[#f9a825] w-[60px] h-[60px] top-2.5 left-2.5 max-md:w-12 max-md:h-12 max-md:top-2 max-md:left-2"
      animate={{ rotate: -360 }}
      transition={{ repeat: Infinity, ease: 'linear', duration: 1.2 }}
      aria-hidden
    />
    <motion.div
      className="absolute rounded-full border-[3px] border-transparent border-b-[#ffe082] w-10 h-10 top-5 left-5 max-md:w-8 max-md:h-8 max-md:top-4 max-md:left-4"
      animate={spin.animate}
      transition={{ ...spin.transition, duration: 0.9 }}
      aria-hidden
    />
    <motion.div
      className="absolute w-4 h-4 top-8 left-8 max-md:w-3 max-md:h-3 max-md:top-[26px] max-md:left-[26px] rounded-full bg-gradient-to-br from-[#feca57] to-[#f9a825]"
      animate={{ scale: [1, 1.2, 1] }}
      transition={{ repeat: Infinity, duration: 1, ease: 'easeInOut' }}
      aria-hidden
    />
  </div>
);

const PracticeLoadingOverlay = ({
  visible = false,
  text = 'Submitting your answers...',
  subtext,
  variant = 'loading',
  onRetry,
  onClose,
  showCloseButton = true,
  retryLabel = 'Try Again',
  closeLabel = 'Go Back',
  bottomNote,
}) => {
  const isError = variant === 'error';
  const displayClose = Boolean(onClose && showCloseButton);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`fixed inset-0 flex justify-center items-center z-[9999] ${
            isError
              ? 'bg-[linear-gradient(135deg,#1a0a0a_0%,#2d1111_50%,#1a0808_100%)]'
              : 'bg-[#12766d] dark:bg-[linear-gradient(135deg,#0a3330_0%,#0d4a42_50%,#072421_100%)]'
          }`}
          role={isError ? 'alert' : 'status'}
          aria-live="polite"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          {isError ? (
            <div className="flex flex-col items-center gap-6 px-6 max-w-md text-center">
              <motion.div
                className="w-20 h-20 max-md:w-16 max-md:h-16 rounded-full bg-[rgba(220,53,69,0.15)] flex items-center justify-center"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <WarningIcon />
              </motion.div>

              <p className="m-0 text-xl font-bold text-white max-md:text-lg">{text}</p>

              {subtext && (
                <p className="m-0 text-sm font-normal text-white/60 leading-relaxed max-md:text-[13px]">
                  {subtext}
                </p>
              )}

              <div className="flex gap-3 mt-2 max-md:flex-col max-md:w-full">
                {onRetry && (
                  <motion.button
                    type="button"
                    onClick={onRetry}
                    className="py-3 px-8 bg-[#12766d] text-white border-none rounded-xl cursor-pointer font-semibold text-base hover:bg-[#0f6159] transition-colors max-md:w-full"
                    whileTap={{ scale: 0.96 }}
                  >
                    {retryLabel}
                  </motion.button>
                )}
                {displayClose && (
                  <motion.button
                    type="button"
                    onClick={onClose}
                    className="py-3 px-8 bg-white/10 text-white border border-white/20 rounded-xl cursor-pointer font-semibold text-base hover:bg-white/20 transition-colors max-md:w-full"
                    whileTap={{ scale: 0.96 }}
                  >
                    {closeLabel}
                  </motion.button>
                )}
              </div>
              {bottomNote && (
                <p className="m-0 mt-2 text-sm font-normal text-white/55 leading-relaxed max-md:text-[13px] max-w-sm">
                  {bottomNote}
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-7">
              <LoadingSpinner />
              <p className="m-0 text-lg font-semibold text-white text-center leading-snug max-md:text-base">
                {text}
              </p>
              {subtext && (
                <p className="-mt-3 text-sm font-normal text-white/70 text-center leading-snug max-md:text-[13px]">
                  {subtext}
                </p>
              )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PracticeLoadingOverlay;
